import React, { createContext, useContext, useState, useEffect } from "react";
import { pb } from "../services/pocketbase";
import { useAuth } from "./AuthContext";
import { getPrazos, getTemposEtapaMap } from "../services/prazosService";
import { getFeriados } from "../services/calendarioService";
import { setFeriadosCache } from "../utils/formatters";

// 1. Cria o Contexto
const ProductionContext = createContext();

// 2. Cria o Provedor (O Componente que segura os dados)
export function ProductionProvider({ children }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prazosModelo, setPrazosModelo] = useState([]);
  const [temposEtapaMap, setTemposEtapaMap] = useState({});
  const [prazosMap, setPrazosMap] = useState({});
  const [feriados, setFeriados] = useState([]);
  const [feriadosSet, setFeriadosSet] = useState(new Set());

  // --- FUNÇÃO PARA BUSCAR PRAZOS, FERIADOS E MATRIZ DE ETAPAS (1x) ---
  const fetchPrazosData = React.useCallback(async () => {
    try {
      const [listPrazos, mapTempos, listFeriados] = await Promise.all([
        getPrazos(),
        getTemposEtapaMap(),
        getFeriados(),
      ]);
      setPrazosModelo(listPrazos);
      setTemposEtapaMap(mapTempos);
      const pMap = {};
      listPrazos.forEach((p) => {
        if (p.modelo) pMap[p.modelo] = p;
      });
      setPrazosMap(pMap);

      setFeriados(listFeriados || []);
      const fSet = new Set(
        (listFeriados || []).filter((f) => f.ativo !== false).map((f) => f.data)
      );
      setFeriadosSet(fSet);
      setFeriadosCache(fSet);

      console.log("⏱️ PRAZOS & FERIADOS OFICINA: Carregados no ProductionContext");
    } catch (err) {
      console.error("Erro ao carregar dados de prazos e feriados no ProductionContext:", err);
    }
  }, []);

  // --- FUNÇÃO PARA BUSCAR DADOS INICIAIS ---
  const fetchInitialData = React.useCallback(async () => {
    try {
      const records = await pb.collection("production_orders").getFullList({
        sort: "+created", // Mais antigo primeiro (asc)
        filter: "archived != true",
        expand: "fiscal_invoices_via_order",
      });

      const mappedOrders = records.map(r => ({
        id: r.id,
        ...r.full_data,
        ...r, // Garante que campos flat novos (status, etc) tenham prioridade se atualizados
        expand: r.expand || {},
        exception: r.specs?.exception || r.exception || r.full_data?.exception || "" // <--- MAPEAMENTO CENTRAL
      }));

      setOrders(mappedOrders);
      setLoading(false);
      console.log("📡 LEITURA POCKETBASE: Lista de Produção Carregada");
    } catch (err) {
      console.error("Erro ao buscar produção:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setOrders([]);
      setPrazosModelo([]);
      setTemposEtapaMap({});
      setPrazosMap({});
      setLoading(true);
      return;
    }

    fetchInitialData();
    fetchPrazosData();

    // --- REALTIME SUBSCRIPTION PRAZOS ---
    pb.collection("oficina_prazos_modelo").subscribe("*", () => {
      fetchPrazosData();
    }).catch(() => {});

    // --- REALTIME SUBSCRIPTION (Substitui onSnapshot) ---
    pb.collection("production_orders").subscribe("*", (e) => {
      console.log("⚡ Realtime Event:", e.action, e.record.id);

      setOrders((prevOrders) => {
        const record = {
          id: e.record.id,
          ...e.record.full_data,
          ...e.record,
          expand: e.record.expand || {},
          exception: e.record.specs?.exception || e.record.exception || e.record.full_data?.exception || ""
        };

        // 1. DELETE ou ARQUIVADO -> Remover
        if (e.action === "delete" || record.archived === true) {
          return prevOrders.filter((o) => o.id !== record.id);
        }

        // 2. CREATE -> Adicionar (se não estiver arquivado)
        if (e.action === "create") {
          // Verifica se já não existe (paranoia) e se não é arquivado
          if (!record.archived) {
            // Precisamos manter a ordenação "+created". 
            // Como é 'create', assumimos que é o mais novo, então vai pro final.
            return [...prevOrders, record];
          }
        }

        // 3. UPDATE -> Atualizar ou Adicionar (se entrou no filtro agora)
        if (e.action === "update") {
          const exists = prevOrders.find((o) => o.id === record.id);

          if (exists) {
            // Se já existe, atualiza, mesclando expand pré-existente se não enviado pelo PB
            return prevOrders.map((o) => (o.id === record.id ? { ...o, ...record, expand: { ...o.expand, ...record.expand } } : o));
          } else if (!record.archived) {
            // (CORREÇÃO) Se não existe mas é válido (desarquivado), adicionamos!
            return [...prevOrders, record];
          }
        }

        return prevOrders;
      });
    });

    // --- REALTIME NOTAS FISCAIS ---
    pb.collection("fiscal_invoices").subscribe("*", (e) => {
      setOrders((prevOrders) => {
        const invoice = e.record;
        const orderId = Array.isArray(invoice.order) ? invoice.order[0] : invoice.order;
        if (!orderId) return prevOrders;

        if (e.action === "delete") {
          return prevOrders.map((o) => {
            if (o.id === orderId) {
              const invoicesList = (o.expand?.fiscal_invoices_via_order || []).filter((i) => i.id !== invoice.id);
              return {
                ...o,
                expand: {
                  ...o.expand,
                  fiscal_invoices_via_order: invoicesList,
                },
              };
            }
            return o;
          });
        }

        if (e.action === "update" || e.action === "create") {
          return prevOrders.map((o) => {
            if (o.id === orderId) {
              const invoicesList = o.expand?.fiscal_invoices_via_order || [];
              const index = invoicesList.findIndex((i) => i.id === invoice.id);
              let newList = [...invoicesList];
              if (index >= 0) {
                newList[index] = invoice;
              } else {
                newList.push(invoice);
              }
              return {
                ...o,
                expand: {
                  ...o.expand,
                  fiscal_invoices_via_order: newList,
                },
              };
            }
            return o;
          });
        }

        return prevOrders;
      });
    });

    return () => {
      pb.collection("production_orders").unsubscribe("*").catch(() => {});
      pb.collection("fiscal_invoices").unsubscribe("*").catch(() => {});
      pb.collection("oficina_prazos_modelo").unsubscribe("*").catch(() => {});
    };
  }, [user, fetchInitialData, fetchPrazosData]);

  return (
    <ProductionContext.Provider
      value={{
        orders,
        setOrders,
        loading,
        refreshOrders: fetchInitialData,
        prazosModelo,
        temposEtapaMap,
        prazosMap,
        feriados,
        feriadosSet,
        reloadFeriados: fetchPrazosData,
        refreshPrazos: fetchPrazosData,
      }}
    >
      {children}
    </ProductionContext.Provider>
  );
}

// 3. O Hook para usar nas abas (Facilita a vida)
export function useProductionContext() {
  return useContext(ProductionContext);
}